import { useEffect, useMemo, useRef, useState } from "react";
import { Activity, AlertTriangle, Pause, Play } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { severityStyles, type FrontendErrorLog } from "@/observability/frontendTypes";
import { useFrontendErrorStream } from "@/hooks/useFrontendErrorStream";

function formatTimestamp(value: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      month: "short",
      day: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function severityLabel(log: FrontendErrorLog): string {
  return log.severity.toUpperCase();
}

export default function RealTimeErrorStreamPanel() {
  const [paused, setPaused] = useState(false);
  const { events, connected, error } = useFrontendErrorStream({ enabled: !paused });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (paused) return;
    if (!containerRef.current) return;
    containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }, [events, paused]);

  const statusText = useMemo(() => {
    if (error) return "Disconnected";
    return connected ? "Live" : "Connecting…";
  }, [connected, error]);

  return (
    <div className="space-y-4 rounded-2xl border border-border/10 bg-background/70 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Activity className={`h-4 w-4 ${connected ? "text-emerald-400" : "text-amber-300"}`} />
            <span>{statusText}</span>
          </div>
          <h2 className="text-2xl font-semibold text-foreground">Real-Time Error Stream</h2>
          <p className="text-sm text-muted-foreground">Newest events from connected clients appear instantly.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setPaused((prev) => !prev)}
            className="gap-2"
          >
            {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            {paused ? "Resume" : "Pause"}
          </Button>
        </div>
      </div>
      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-primary-foreground dark:text-amber-100">
          <AlertTriangle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
      <div
        ref={containerRef}
        className="max-h-[420px] space-y-3 overflow-y-auto rounded-xl border border-border/5 bg-background/80 p-3"
      >
        {events.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {paused ? "Stream paused." : "Waiting for new errors…"}
          </p>
        ) : (
          events.map((log, index) => (
            <div
              key={`${log.id}-${index}`}
              className="rounded-xl border border-border/5 bg-background/5 px-3 py-2 text-sm text-foreground"
            >
              <div className="flex items-center justify-between gap-3">
                <Badge className={severityStyles[log.severity]}>{severityLabel(log)}</Badge>
                <span className="text-xs text-muted-foreground">{formatTimestamp(log.created_at)}</span>
              </div>
              <p className="mt-1 text-base font-semibold leading-snug">{log.message}</p>
                <div className="mt-1 text-xs text-muted-foreground flex flex-wrap items-center gap-2">
                  <span>{log.route || "Unknown route"}</span>
                  {log.version && (
                    <span className="rounded border border-border/10 bg-background/5 px-2 py-0.5 text-[10px] uppercase tracking-wide">
                      build {log.version}
                    </span>
                  )}
                </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
